/**
 * Bot Service
 * Handles all bot-related API operations
 */

import api from '@services/api';
import type { Bot, CreateBotPayload, UpdateBotPayload, BotAttachment } from '@/types/models';

class BotService {
  /**
   * Get all bots for current tenant
   */
  async getBots(): Promise<Bot[]> {
    const { data } = await api.get('/chatbots?per_page=100');
    return data.chatbots || [];
  }

  /**
   * Only the agents that can AUTHOR a design-skill bundle.
   *
   * Filtered in SQL, not here. `getBots` returns ONE page (100); a tenant
   * with more agents than that simply does not contain the studio in its
   * response, and a caller filtering client-side concludes the tenant has
   * none. That is exactly what hid the Brand Book entrance on a tenant with
   * 112 agents (2026-09-23).
   */
  async getBrandbookAuthoringBots(): Promise<Bot[]> {
    const { data } = await api.get('/chatbots?brandbook_authoring=true&per_page=100');
    return data.chatbots || [];
  }

  /**
   * Lightweight bot list for the home-page carousel — minimal payload
   * (no permissions, no shares, no data bundles, no system prompts) and
   * Redis-cached per (tenant, user) on the server. Use this whenever the
   * caller only needs id/name/description/avatar to render a chooser.
   */
  async getCarouselBots(): Promise<Bot[]> {
    const { data } = await api.get('/chatbots/carousel');
    return data.chatbots || [];
  }

  /**
   * Get a specific bot by ID
   */
  async getBot(id: string): Promise<Bot> {
    const { data } = await api.get(`/chatbots/${id}`);
    return data.chatbot;
  }

  /**
   * Create a new bot
   */
  async createBot(payload: CreateBotPayload): Promise<Bot> {
    const { data } = await api.post('/chatbots', payload);
    return data.chatbot;
  }

  /**
   * Update an existing bot
   */
  async updateBot(id: string, payload: Omit<UpdateBotPayload, 'chatbot_id'>): Promise<Bot> {
    const { data } = await api.put(`/chatbots/${id}`, payload);
    return data.chatbot;
  }

  /**
   * Delete a bot
   */
  async deleteBot(id: string): Promise<void> {
    await api.delete(`/chatbots/${id}`);
  }

  async duplicateBot(id: string): Promise<Bot> {
    const { data } = await api.post(`/chatbots/${id}/duplicate`);
    return data.chatbot;
  }

  /**
   * Get favorite bots
   */
  async getFavoriteBots(): Promise<Bot[]> {
    const { data } = await api.get('/chatbots/favorites');
    return data.chatbots || [];
  }

  /**
   * Add bot to favorites
   */
  async addFavorite(id: string): Promise<void> {
    await api.post(`/chatbots/${id}/favorite`);
  }

  /**
   * Remove bot from favorites
   */
  async removeFavorite(id: string): Promise<void> {
    await api.delete(`/chatbots/${id}/favorite`);
  }

  /**
   * Toggle bot favorite status based on current state
   */
  async toggleFavorite(id: string, currentlyFavorited: boolean): Promise<void> {
    if (currentlyFavorited) {
      await this.removeFavorite(id);
    } else {
      await this.addFavorite(id);
    }
  }

  /**
   * Get recent used bots
   */
  async getRecentBots(limit: number = 10): Promise<Bot[]> {
    const { data } = await api.get(`/chatbots/recent?limit=${limit}`);
    return data.chatbots || [];
  }

  // ===== Bot Context Files (Attachments) =====

  /**
   * Get all context files attached to a bot
   */
  async getBotAttachments(botId: string): Promise<BotAttachment[]> {
    const { data } = await api.get(`/chatbots/${botId}/attachments`);
    return data.attachments || [];
  }

  /**
   * Upload a context file to a bot
   */
  async uploadBotFile(
    botId: string,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<BotAttachment> {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await api.post(`/chatbots/${botId}/attachments`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
    });
    return data.attachment;
  }

  /**
   * Delete a context file from a bot
   */
  async deleteBotFile(botId: string, attachmentId: string): Promise<void> {
    await api.delete(`/chatbots/${botId}/attachments/${attachmentId}`);
  }

  /**
   * Toggle include_as_context for a bot attachment
   */
  async updateBotAttachment(
    botId: string,
    attachmentId: string,
    payload: { include_as_context: boolean }
  ): Promise<BotAttachment> {
    const { data } = await api.patch(`/chatbots/${botId}/attachments/${attachmentId}`, payload);
    return data.attachment;
  }

  // ===== Data Bundle Context Toggle =====

  /**
   * Toggle include_as_context for a bot's data bundle
   */
  async updateBotDataBundle(
    botId: string,
    bundleId: string,
    payload: { include_as_context: boolean }
  ): Promise<{ success: boolean; include_as_context: boolean }> {
    const { data } = await api.patch(`/chatbots/${botId}/data-bundles/${bundleId}`, payload);
    return data;
  }

  // ===== Bot Sharing =====

  /**
   * Get all shares for a bot (only owner can access)
   */
  async getBotShares(botId: string): Promise<{ chatbot_id: string; chatbot_name: string; shares: BotShare[] }> {
    const { data } = await api.get(`/chatbots/${botId}/shares`);
    return data;
  }
}

// Bot share type
export interface BotShare {
  share_id: string;
  chatbot_id: string;
  shared_with_user_id: string | null;
  shared_with_user_email: string | null;
  shared_with_user_name: string | null;
  shared_with_tenant_id: string | null;
  shared_with_tenant_name: string | null;
  shared_by_user_id: string;
  shared_by_user_email: string | null;
  permission_level: 'view' | 'chat' | 'edit';
  can_reshare: boolean;
  shared_at: string;
  share_type: 'user' | 'tenant';
}

// Create and export singleton instance
const botService = new BotService();

export default botService;
